import {
  CognitoUserPool,
  CognitoUser,
  AuthenticationDetails,
  CognitoUserAttribute,
} from 'amazon-cognito-identity-js';

const userPoolId = import.meta.env.VITE_USER_POOL_ID;
const clientId = import.meta.env.VITE_USER_POOL_CLIENT_ID;

// Don't throw error at module load - check at runtime instead
if (!userPoolId || !clientId) {
  console.error('Missing Cognito configuration. Please check .env file.');
  console.error('VITE_USER_POOL_ID:', userPoolId);
  console.error('VITE_USER_POOL_CLIENT_ID:', clientId);
}

// Create userPool only if config is available, otherwise create a dummy one
let userPool: CognitoUserPool | null = null;
if (userPoolId && clientId) {
  userPool = new CognitoUserPool({
    UserPoolId: userPoolId,
    ClientId: clientId,
  });
} else {
  console.warn('Cognito UserPool not initialized - using dummy pool');
  // Create a dummy pool that will fail gracefully at runtime
  userPool = new CognitoUserPool({
    UserPoolId: 'dummy',
    ClientId: 'dummy',
  });
}

export interface AuthUser {
  username: string;
  email: string;
}

export const signUp = (email: string, password: string): Promise<void> => {
  return new Promise((resolve, reject) => {
    const attributeList = [
      new CognitoUserAttribute({
        Name: 'email',
        Value: email,
      }),
    ];

    userPool.signUp(email, password, attributeList, [], (err) => {
      if (err) {
        reject(err);
        return;
      }
      resolve();
    });
  });
};

export const confirmSignUp = (email: string, code: string): Promise<void> => {
  return new Promise((resolve, reject) => {
    const cognitoUser = new CognitoUser({
      Username: email,
      Pool: userPool,
    });

    cognitoUser.confirmRegistration(code, true, (err) => {
      if (err) {
        reject(err);
        return;
      }
      resolve();
    });
  });
};

export const resendConfirmationCode = (email: string): Promise<void> => {
  return new Promise((resolve, reject) => {
    const cognitoUser = new CognitoUser({
      Username: email,
      Pool: userPool,
    });

    cognitoUser.resendConfirmationCode((err) => {
      if (err) {
        reject(err);
        return;
      }
      resolve();
    });
  });
};

export const signIn = (email: string, password: string): Promise<string> => {
  return new Promise((resolve, reject) => {
    const authenticationDetails = new AuthenticationDetails({
      Username: email,
      Password: password,
    });

    const cognitoUser = new CognitoUser({
      Username: email,
      Pool: userPool,
    });

    cognitoUser.authenticateUser(authenticationDetails, {
      onSuccess: (result) => {
        const idToken = result.getIdToken().getJwtToken();
        resolve(idToken);
      },
      onFailure: (err) => {
        reject(err);
      },
    });
  });
};

export const signOut = (): void => {
  const cognitoUser = userPool.getCurrentUser();
  if (cognitoUser) {
    cognitoUser.signOut();
  }
};

export const getCurrentUser = (): Promise<AuthUser | null> => {
  return new Promise((resolve) => {
    const cognitoUser = userPool.getCurrentUser();

    if (!cognitoUser) {
      resolve(null);
      return;
    }

    cognitoUser.getSession((err: any, session: any) => {
      if (err || !session.isValid()) {
        resolve(null);
        return;
      }

      cognitoUser.getUserAttributes((err, attributes) => {
        if (err) {
          resolve(null);
          return;
        }

        const email = attributes?.find((attr) => attr.Name === 'email')?.Value || '';

        resolve({
          username: cognitoUser.getUsername(),
          email,
        });
      });
    });
  });
};

export const getIdToken = (): Promise<string | null> => {
  return new Promise((resolve) => {
    const cognitoUser = userPool.getCurrentUser();

    if (!cognitoUser) {
      console.warn('⚠️ No Cognito user found - user not signed in');
      resolve(null);
      return;
    }

    cognitoUser.getSession((err: any, session: any) => {
      if (err) {
        console.error('❌ Error getting Cognito session:', err);
        console.error('❌ Error details:', {
          message: err.message,
          code: err.code,
          name: err.name
        });
        resolve(null);
        return;
      }

      if (!session) {
        console.warn('⚠️ No session returned from Cognito');
        resolve(null);
        return;
      }

      if (!session.isValid()) {
        console.warn('⚠️ Cognito session is invalid or expired');
        console.warn('⚠️ Session validity:', {
          isValid: session.isValid(),
          idToken: session.getIdToken() ? 'exists' : 'missing',
          accessToken: session.getAccessToken() ? 'exists' : 'missing'
        });
        // Session is invalid - user needs to sign in again
        resolve(null);
        return;
      }

      // Use ID Token for AgentCore authentication (as per AWS documentation)
      try {
        const idToken = session.getIdToken().getJwtToken();
        console.log('✅ Got ID token, length:', idToken.length);
        
        // Decode and log token info for debugging
        try {
          const parts = idToken.split('.');
          if (parts.length >= 2) {
            const payload = JSON.parse(atob(parts[1].replace(/-/g, '+').replace(/_/g, '/')));
            const exp = payload.exp;
            const now = Math.floor(Date.now() / 1000);
            console.log('🔍 ID Token info:', {
              email: payload.email,
              client_id: payload.client_id,
              token_use: payload.token_use,
              expires_in: exp ? `${exp - now} seconds` : 'unknown',
              is_expired: exp ? exp < now : 'unknown'
            });
          }
        } catch (decodeErr) {
          console.warn('Could not decode token for logging:', decodeErr);
        }
        
        resolve(idToken);
      } catch (tokenErr) {
        console.error('❌ Error getting ID token from session:', tokenErr);
        resolve(null);
      }
    });
  });
};

export const getAccessToken = (): Promise<string | null> => {
  return new Promise((resolve) => {
    const cognitoUser = userPool.getCurrentUser();

    if (!cognitoUser) {
      resolve(null);
      return;
    }

    cognitoUser.getSession((err: any, session: any) => {
      if (err || !session.isValid()) {
        resolve(null);
        return;
      }

      // Return the access token
      resolve(session.getAccessToken().getJwtToken());
    });
  });
};
